"use client";

import { useState, useCallback, useEffect } from "react";
import Link from "next/link";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  ArrowRight,
  AlertTriangle,
  TrendingUp,
  Star,
  CheckCircle2,
} from "lucide-react";
import type { HealthCheckStats } from "@/lib/review-health-stats";

interface Props {
  isSignedIn: boolean;
}

interface SessionLocation {
  name: string;
  title: string;
  address: string;
  placeId: string;
  category: string;
}

type State = "landing" | "connect" | "picker" | "loading" | "results" | "error";

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4 },
};

function formatTime(hours: number | null): string {
  if (hours === null) return "N/A";
  if (hours < 1) return `${Math.round(hours * 60)} min`;
  if (hours < 48) return `${Math.round(hours)} hrs`;
  return `${Math.round(hours / 24)} days`;
}

function gradeColor(grade: string): string {
  if (grade === "A" || grade === "B") return "text-green-600";
  if (grade === "C") return "text-yellow-600";
  return "text-red-600";
}

function gradeRingStroke(grade: string): string {
  if (grade === "A" || grade === "B") return "stroke-green-500";
  if (grade === "C") return "stroke-yellow-500";
  return "stroke-red-500";
}

function gbpErrorMessage(code: string): string {
  if (code === "gbp_denied") {
    return "Google Business Profile access was not granted. Reconnect and allow review access to run the report.";
  }
  if (code === "gbp_no_offline") {
    return "Google did not grant offline access. Reconnect and allow all requested permissions.";
  }
  if (code === "gbp_init_failed") {
    return "We could not start the Google connection. Please try again.";
  }
  return "We could not connect your Google Business Profile. Please try again.";
}

function RateBar({ rate, label }: { rate: number; label: string }) {
  const pct = Math.round(rate * 100);
  return (
    <div className="flex items-center gap-3">
      <span className="text-sm text-ink-500 w-24 shrink-0">{label}</span>
      <div className="flex-1 h-3 bg-ink-100 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full bg-brand-600 transition-all duration-700"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-sm font-semibold text-ink-900 w-12 text-right">
        {pct}%
      </span>
    </div>
  );
}

function CircularGauge({ value, grade }: { value: number; grade: string }) {
  const pct = Math.round(value * 100);
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (pct / 100) * circumference;

  return (
    <div className="relative w-40 h-40 mx-auto">
      <svg className="w-full h-full -rotate-90" viewBox="0 0 128 128">
        <circle
          cx="64"
          cy="64"
          r={radius}
          fill="none"
          strokeWidth="10"
          className="stroke-ink-100"
        />
        <circle
          cx="64"
          cy="64"
          r={radius}
          fill="none"
          strokeWidth="10"
          className={`${gradeRingStroke(grade)} transition-[stroke-dashoffset] duration-1000`}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`text-3xl font-black ${gradeColor(grade)}`}>
          {pct}%
        </span>
        <span className={`text-lg font-bold ${gradeColor(grade)}`}>
          {grade}
        </span>
      </div>
    </div>
  );
}

export default function HealthCheckClient({ isSignedIn }: Props) {
  const [state, setState] = useState<State>(
    isSignedIn ? "connect" : "landing"
  );
  const [locations, setLocations] = useState<SessionLocation[]>([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [stats, setStats] = useState<HealthCheckStats | null>(null);
  const [error, setError] = useState("");

  const checkGbpSession = useCallback(async () => {
    try {
      const res = await fetch("/api/gbp/session");
      if (!res.ok) {
        setState("connect");
        return;
      }
      const data = await res.json();
      if (data.locations?.length > 0) {
        setError("");
        setLocations(data.locations);
        if (data.locations.length === 1) {
          setSelectedLocation(data.locations[0].name);
        }
        setState("picker");
      } else {
        setState("connect");
      }
    } catch {
      setState("connect");
    }
  }, []);

  useEffect(() => {
    if (isSignedIn && typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const gbpError = params.get("gbp_error");
      if (gbpError) {
        setError(gbpErrorMessage(gbpError));
        setState("connect");
        return;
      }
      void checkGbpSession();
    }
  }, [isSignedIn, checkGbpSession]);

  const analyze = async () => {
    if (!selectedLocation) return;
    setState("loading");
    setError("");

    try {
      const res = await fetch("/api/tools/health-check", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ locationName: selectedLocation }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || "Something went wrong");
        setState("error");
        return;
      }

      const data = (await res.json()) as HealthCheckStats;
      setStats(data);
      setState("results");
    } catch {
      setError("Connection error");
      setState("error");
    }
  };

  // ---------- Landing (not signed in) ----------
  if (state === "landing") {
    return (
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-3xl px-6">
          <motion.div {...fadeIn} className="text-center">
            <p className="ribbon mb-4 text-ink-500">Free Review Health Check</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-ink-950 leading-tight mb-6 text-balance">
              How fast do you reply to Google{" "}
              <em className="font-display italic font-normal text-brand-600">
                reviews?
              </em>
            </h1>
            <p className="text-lg text-ink-600 max-w-xl mx-auto mb-10 text-pretty">
              Connect your Google Business Profile and get an instant report on
              your review response rate, reply speed, and a letter grade — free.
            </p>
            <ul className="text-left max-w-md mx-auto space-y-3 mb-10">
              {[
                "See your response rate by star rating",
                "Check your average reply speed",
                "Get a grade (A-F) compared to best practices",
              ].map((text) => (
                <li
                  key={text}
                  className="flex items-start gap-3 rounded-xl border border-ink-100 bg-white p-3.5"
                >
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-accent-600" />
                  <span className="text-ink-700">{text}</span>
                </li>
              ))}
            </ul>
            <p className="text-sm text-ink-500 mb-5">
              Sign in with Google to get started (we only read reviews — nothing
              is changed).
            </p>
            <Link
              href="/auth/signin?callbackUrl=/tools/review-health-check"
              className="inline-flex items-center gap-2 rounded-2xl bg-brand-600 px-8 py-4 text-sm font-bold text-white shadow-lg shadow-brand-100 hover:bg-brand-700 transition-colors"
            >
              Check My Reviews
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    );
  }

  // ---------- Connect GBP ----------
  if (state === "connect") {
    return (
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-2xl px-6 text-center">
          <motion.div {...fadeIn}>
            <div className="aura mx-auto mb-8 flex h-16 w-16 items-center justify-center rounded-2xl border border-ink-100 bg-white shadow-sm">
              <ShieldCheck className="h-8 w-8 text-brand-600" />
            </div>
            <p className="ribbon mb-3 text-ink-500">Step 1 of 2</p>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-ink-950 mb-4 text-balance">
              Connect your{" "}
              <em className="font-display italic font-normal text-brand-600">
                Google Business Profile
              </em>
            </h1>
            <p className="text-ink-600 mb-10 max-w-lg mx-auto text-pretty">
              We only read your reviews — nothing is modified.
            </p>
            {error && (
              <p className="mb-6 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
                {error}
              </p>
            )}
            <Link
              href="/api/auth/gbp?returnUrl=/tools/review-health-check"
              className="inline-flex items-center gap-2 rounded-2xl bg-brand-600 px-8 py-4 text-sm font-bold text-white shadow-lg shadow-brand-100 hover:bg-brand-700 transition-colors"
            >
              Connect Google Business Profile
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    );
  }

  // ---------- Location picker ----------
  if (state === "picker") {
    return (
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-2xl px-6">
          <motion.div
            {...fadeIn}
            className="bg-white rounded-2xl border border-ink-200 shadow-sm p-8"
          >
            <h2 className="text-xl font-bold text-ink-900 mb-2">
              Select a location
            </h2>
            {locations.length > 1 && (
              <p className="text-sm text-ink-500 mb-6">
                Choose which location you&apos;d like to analyze.
              </p>
            )}
            <div className="space-y-3 mb-6">
              {locations.map((loc) => (
                <label
                  key={loc.name}
                  className={`flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition-colors ${
                    selectedLocation === loc.name
                      ? "border-brand-600 bg-brand-50"
                      : "border-ink-200 hover:bg-ink-50"
                  }`}
                >
                  <input
                    type="radio"
                    name="location"
                    value={loc.name}
                    checked={selectedLocation === loc.name}
                    onChange={(e) => setSelectedLocation(e.target.value)}
                    className="accent-brand-600"
                  />
                  <div>
                    <p className="font-semibold text-ink-900">{loc.title}</p>
                    {loc.address && (
                      <p className="text-sm text-ink-500">{loc.address}</p>
                    )}
                    {loc.category && (
                      <p className="text-xs text-ink-400">{loc.category}</p>
                    )}
                  </div>
                </label>
              ))}
            </div>
            <button
              onClick={analyze}
              disabled={!selectedLocation}
              className="w-full rounded-xl bg-brand-600 px-6 py-3 text-sm font-bold text-white shadow-sm hover:bg-brand-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Analyze Reviews
            </button>
          </motion.div>
        </div>
      </section>
    );
  }

  // ---------- Loading ----------
  if (state === "loading") {
    return (
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-2xl px-6 text-center">
          <motion.div {...fadeIn}>
            <div className="animate-pulse space-y-6">
              <div className="w-40 h-40 rounded-full bg-ink-100 mx-auto" />
              <div className="h-6 bg-ink-100 rounded w-1/2 mx-auto" />
              <div className="h-4 bg-ink-100 rounded w-2/3 mx-auto" />
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="h-3 bg-ink-100 rounded" />
                ))}
              </div>
            </div>
            <p className="text-ink-500 mt-6">
              Analyzing your reviews&hellip;
            </p>
          </motion.div>
        </div>
      </section>
    );
  }

  // ---------- Error ----------
  if (state === "error") {
    const isSessionExpired =
      error.includes("session") || error.includes("expired");
    return (
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-2xl px-6 text-center">
          <motion.div {...fadeIn}>
            <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
            <p className="text-ink-900 font-semibold mb-4">
              {isSessionExpired
                ? "Your session has expired. Please reconnect."
                : error}
            </p>
            {isSessionExpired ? (
              <Link
                href="/api/auth/gbp?returnUrl=/tools/review-health-check"
                className="inline-flex items-center gap-2 rounded-xl bg-brand-600 px-8 py-3 text-sm font-bold text-white shadow-md hover:bg-brand-700 transition-colors"
              >
                Reconnect
                <ArrowRight className="w-5 h-5" />
              </Link>
            ) : (
              <button
                onClick={() => setState("picker")}
                className="rounded-xl border border-ink-300 px-6 py-3 text-sm font-semibold text-ink-700 hover:bg-ink-50 transition-colors"
              >
                Try Again
              </button>
            )}
          </motion.div>
        </div>
      </section>
    );
  }

  // ---------- Results ----------
  if (state === "results" && stats) {
    const pct = Math.round(stats.responseRate * 100);

    if (stats.reviewsAnalyzed === 0) {
      return (
        <section className="py-16 md:py-24">
          <div className="mx-auto max-w-2xl px-6 text-center">
            <motion.div {...fadeIn}>
              <Star className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-ink-900 mb-4">
                No reviews found
              </h2>
              <p className="text-ink-600 mb-6">
                This location doesn&apos;t have any Google reviews yet.
              </p>
              <Link
                href="/onboarding"
                className="inline-flex items-center gap-2 rounded-xl bg-brand-600 px-8 py-3 text-sm font-bold text-white shadow-md hover:bg-brand-700 transition-colors"
              >
                Get Started with OneTapReply
                <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
          </div>
        </section>
      );
    }

    return (
      <section className="py-12 md:py-20">
        <div className="mx-auto max-w-4xl px-6">
          <motion.div {...fadeIn}>
            <div className="text-center mb-12">
              <p className="ribbon mb-3 text-ink-500">Report</p>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-ink-950 text-balance">
                Your Review{" "}
                <em className="font-display italic font-normal text-brand-600">
                  Health Report
                </em>
              </h2>
            </div>

            {/* Top metrics */}
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {/* Response rate gauge */}
              <div className="bg-white rounded-2xl border border-ink-200 shadow-sm p-6 text-center">
                <p className="text-sm text-ink-500 mb-4">Response Rate</p>
                <CircularGauge value={stats.responseRate} grade={stats.grade} />
              </div>

              {/* Avg response time */}
              <div className="bg-white rounded-2xl border border-ink-200 shadow-sm p-6 text-center flex flex-col justify-center">
                <p className="text-sm text-ink-500 mb-2">
                  Avg. Response Time
                </p>
                <p className="text-3xl font-black text-ink-900">
                  {formatTime(stats.avgResponseTimeHours)}
                </p>
                <p className="text-xs text-ink-400 mt-2">
                  Median: {formatTime(stats.medianResponseTimeHours)}
                </p>
              </div>

              {/* Summary stats */}
              <div className="bg-white rounded-2xl border border-ink-200 shadow-sm p-6 space-y-4">
                <div className="flex justify-between">
                  <span className="text-sm text-ink-500">Total Reviews</span>
                  <span className="font-bold text-ink-900">
                    {stats.totalReviewCount}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-ink-500">Avg. Rating</span>
                  <span className="font-bold text-ink-900">
                    {stats.averageRating.toFixed(1)} &#9733;
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-ink-500">
                    Reviews Analyzed
                  </span>
                  <span className="font-bold text-ink-900">
                    {stats.reviewsAnalyzed}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-ink-500">Grade</span>
                  <span
                    className={`font-black text-lg ${gradeColor(stats.grade)}`}
                  >
                    {stats.grade}
                  </span>
                </div>
              </div>
            </div>

            {/* Perfect score */}
            {pct === 100 && (
              <motion.div
                {...fadeIn}
                className="bg-green-50 rounded-2xl p-6 mb-8 text-center"
              >
                <CheckCircle2 className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <p className="font-semibold text-ink-900">
                  Perfect response rate!
                </p>
                <p className="text-sm text-ink-600 mt-1">
                  Now let&apos;s work on speed — respond within hours, not days.
                </p>
              </motion.div>
            )}

            {/* Unreplied negative alert */}
            {stats.unrepliedNegativeCount > 0 && (
              <motion.div
                {...fadeIn}
                className="bg-red-50 rounded-2xl p-5 mb-8 flex items-center gap-4"
              >
                <AlertTriangle className="w-6 h-6 text-red-600 shrink-0" />
                <p className="text-red-700 font-semibold">
                  {stats.unrepliedNegativeCount} negative review
                  {stats.unrepliedNegativeCount > 1 ? "s" : ""} without a reply
                  — these hurt your reputation the most.
                </p>
              </motion.div>
            )}

            {/* By-star breakdown */}
            <div className="bg-white rounded-2xl border border-ink-200 shadow-sm p-6 mb-8">
              <h3 className="font-bold text-ink-900 mb-4">
                Response Rate by Star Rating
              </h3>
              <div className="space-y-3">
                {([5, 4, 3, 2, 1] as const).map((star) => {
                  const s = stats.byStarRating[star];
                  return (
                    <RateBar
                      key={star}
                      rate={s.rate}
                      label={`${star} star (${s.replied}/${s.total})`}
                    />
                  );
                })}
              </div>
            </div>

            {/* Trend */}
            {(stats.recentTrend.last30Days !== null ||
              stats.recentTrend.last90Days !== null) && (
              <div className="bg-white rounded-2xl border border-ink-200 shadow-sm p-6 mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="w-5 h-5 text-brand-600" />
                  <h3 className="font-bold text-ink-900">Recent Trend</h3>
                </div>
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center">
                    <p className="text-sm text-ink-500">Last 30 Days</p>
                    <p className="text-2xl font-bold text-ink-900">
                      {stats.recentTrend.last30Days !== null
                        ? `${Math.round(stats.recentTrend.last30Days * 100)}%`
                        : "N/A"}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-ink-500">Last 90 Days</p>
                    <p className="text-2xl font-bold text-ink-900">
                      {stats.recentTrend.last90Days !== null
                        ? `${Math.round(stats.recentTrend.last90Days * 100)}%`
                        : "N/A"}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Side-by-side comparison cards */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {/* You Now */}
              <div className="rounded-2xl border border-ink-200 bg-ink-50 p-6">
                <h3 className="text-sm font-bold text-ink-500 uppercase tracking-wider mb-5">
                  You Now
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-ink-600">Response Rate</span>
                    <span className="text-lg font-black text-ink-900">
                      {pct}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-ink-600">
                      Avg. Reply Speed
                    </span>
                    <span className="text-lg font-black text-ink-900">
                      {formatTime(stats.avgResponseTimeHours)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-ink-600">
                      Unreplied Negative
                    </span>
                    <span className="text-lg font-black text-red-600">
                      {stats.unrepliedNegativeCount}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-ink-600">Grade</span>
                    <span
                      className={`text-lg font-black ${gradeColor(stats.grade)}`}
                    >
                      {stats.grade}
                    </span>
                  </div>
                </div>
              </div>

              {/* With OneTapReply */}
              <div className="rounded-2xl border-2 border-green-200 bg-green-50 p-6 relative">
                <div className="absolute -top-3 right-4 bg-green-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                  With OneTapReply
                </div>
                <h3 className="text-sm font-bold text-green-700 uppercase tracking-wider mb-5">
                  Your New Score
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-green-700">
                      Response Rate
                    </span>
                    <span className="text-lg font-black text-green-700">
                      100%
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-green-700">
                      Avg. Reply Speed
                    </span>
                    <span className="text-lg font-black text-green-700">
                      &lt; 1 hour
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-green-700">
                      Unreplied Negative
                    </span>
                    <span className="text-lg font-black text-green-700">0</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-green-700">Grade</span>
                    <span className="text-lg font-black text-green-700">A</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Conversion CTA */}
            <div className="rounded-2xl bg-gradient-to-br from-brand-700 to-indigo-900 p-8 md:p-10 text-center">
              <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-white mb-3 text-balance">
                {stats.grade === "A" ? (
                  <>
                    Keep your A —{" "}
                    <em className="font-display italic font-normal">
                      automate it.
                    </em>
                  </>
                ) : (
                  <>
                    Want to go from {stats.grade} to{" "}
                    <em className="font-display italic font-normal">A?</em>
                  </>
                )}
              </h3>
              <p className="text-brand-100 mb-8 max-w-lg mx-auto">
                OneTapReply drafts an AI reply for every new review. Approve
                with one tap — 3 posts free every 30 days, then $19.99/mo per
                location.
              </p>
              <Link
                href="/onboarding"
                className="inline-flex items-center gap-2 rounded-2xl bg-white px-10 py-4 text-lg font-bold text-brand-700 shadow-xl hover:bg-brand-50 hover:scale-105 transition-all"
              >
                Start free — automate every reply
                <ArrowRight className="w-5 h-5" />
              </Link>
              <p className="mt-4 text-xs text-brand-200">
                No credit card required. Cancel anytime.
              </p>
            </div>
          </motion.div>
        </div>

        {/* SEO content */}
        <div className="mx-auto max-w-3xl px-6 mt-20 space-y-10 text-ink-700 leading-relaxed">
          <section>
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-ink-950 text-balance">
              Why Your Review Response Rate Matters
            </h2>
            <p className="mt-4">
              Google has confirmed that responding to reviews is a local SEO
              ranking factor. Businesses that consistently reply to reviews
              appear higher in Google Maps and local pack results. Beyond
              rankings, 88% of consumers say they&apos;re more likely to visit a
              business that responds to reviews.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-ink-950 text-balance">
              How Review Reply Speed Affects Your Business
            </h2>
            <p className="mt-4">
              Speed matters as much as the response itself. Replying within a
              few hours signals to both Google and potential customers that
              you&apos;re an active, engaged business. Studies show that
              businesses responding within 24 hours see measurably higher
              customer satisfaction and return rates.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-ink-950 text-balance">
              Free Tool vs. Full Automation
            </h2>
            <p className="mt-4">
              This free checker shows you where you stand. But improving your
              score manually means checking Google Business Profile constantly.
              OneTapReply automates the entire process: it monitors your
              reviews, generates AI replies, and surfaces them in your
              dashboard. You approve with one tap and the reply is posted to
              Google instantly. 3 posts free every 30 days, then $19.99/mo per
              location.
            </p>
          </section>
        </div>

        <script
          type="application/ld+json"
          suppressHydrationWarning
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebApplication",
              name: "Free Review Reply Rate & Speed Checker",
              url: "https://onetapreply.com/tools/review-health-check",
              applicationCategory: "BusinessApplication",
              operatingSystem: "All",
              offers: {
                "@type": "Offer",
                price: "0",
                priceCurrency: "USD",
              },
              description:
                "Check your Google review response rate and reply speed for free. Get a letter grade and see how you compare to best practices.",
            }),
          }}
        />
      </section>
    );
  }

  return null;
}
